import { useState } from "react";
import { Check, Trash2, MailOpen, Reply, AlertCircle } from "lucide-react";
import { ReplyForm, ReplySent, formatDate } from "./ReplyPanel";

const DAY_MS = 24 * 60 * 60 * 1000;
export const isStale = (m) =>
  !m.read && Date.now() - new Date(m.created_at).getTime() > 2 * DAY_MS;

const daysWaiting = (iso) =>
  Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS);

export const MessageRow = ({ message, onMarkRead, onDelete, onReply }) => {
  const [replying, setReplying] = useState(false);
  const stale = isStale(message);

  return (
    <li
      className={`card-flat p-5 md:p-8 ${message.read ? "opacity-80" : ""} ${
        stale ? "border-amber-500/60" : ""
      }`}
      data-testid={`admin-message-${message.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            {!message.read && (
              <span
                className="w-2 h-2 rounded-full bg-ink-900 shrink-0"
                data-testid={`admin-unread-dot-${message.id}`}
              />
            )}
            <span className="font-display text-lg md:text-xl tracking-tight">
              {message.name}
            </span>
            <a
              href={`mailto:${message.email}`}
              className="text-[13px] text-ink-500 underline-grow break-all"
            >
              {message.email}
            </a>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            {message.subject && (
              <span className="text-[10px] font-mono tracking-[0.18em] uppercase px-3 py-1 border border-ink-200 rounded-full text-ink-700">
                {message.subject}
              </span>
            )}
            {message.budget && (
              <span className="text-[10px] font-mono tracking-[0.18em] uppercase px-3 py-1 border border-ink-200 rounded-full text-ink-500" data-testid={`admin-budget-${message.id}`}>
                {message.budget}
              </span>
            )}
            {message.timeline && (
              <span className="text-[10px] font-mono tracking-[0.18em] uppercase px-3 py-1 border border-ink-200 rounded-full text-ink-500" data-testid={`admin-timeline-${message.id}`}>
                {message.timeline}
              </span>
            )}
            <span className="overline">{formatDate(message.created_at)}</span>
            {stale && (
              <span
                className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-[0.14em] uppercase px-3 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300"
                data-testid={`admin-stale-badge-${message.id}`}
              >
                <AlertCircle className="w-3 h-3" strokeWidth={1.8} />
                In attesa da {daysWaiting(message.created_at)} giorni
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!message.reply_text && (
            <button
              onClick={() => setReplying(!replying)}
              data-testid={`admin-reply-btn-${message.id}`}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-ink-200 hover:border-ink-900 text-[11px] font-mono tracking-[0.14em] uppercase transition-colors"
              title="Rispondi"
            >
              <Reply className="w-3.5 h-3.5" strokeWidth={1.6} /> Rispondi
            </button>
          )}
          {!message.read ? (
            <button
              onClick={() => onMarkRead(message.id)}
              data-testid={`admin-mark-read-${message.id}`}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-ink-200 hover:border-ink-900 text-[11px] font-mono tracking-[0.14em] uppercase transition-colors"
              title="Segna come letto"
            >
              <Check className="w-3.5 h-3.5" strokeWidth={1.6} /> Letto
            </button>
          ) : (
            <span className="inline-flex items-center gap-2 text-[11px] font-mono tracking-[0.14em] uppercase text-ink-500">
              <MailOpen className="w-3.5 h-3.5" strokeWidth={1.6} /> Letto
            </span>
          )}
          <button
            onClick={() => onDelete(message.id)}
            data-testid={`admin-delete-${message.id}`}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-ink-200 hover:border-red-600 hover:text-red-600 transition-colors"
            title="Elimina"
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
          </button>
        </div>
      </div>

      <p className="mt-4 text-[14px] md:text-[15px] text-ink-700 leading-relaxed whitespace-pre-wrap border-l-2 border-ink-200 pl-4">
        {message.message}
      </p>

      {message.reply_text && <ReplySent message={message} />}
      {replying && !message.reply_text && (
        <ReplyForm
          message={message}
          onReply={onReply}
          onClose={() => setReplying(false)}
        />
      )}
    </li>
  );
};
